import React, { useState, useEffect, useRef } from "react";
import Translator, { __ } from "services/Translator";
import { horizontalItems } from "services/categories";
import { MENU_ICONS, badgePill, resolveAccent } from "services/menuAppearance";
import { Link, useHistory, useLocation } from "react-router-dom";

export default function StakeGameMenu(props) {
  const history = useHistory();
  const location = useLocation();
  // Navigation arrows state
  const scrollContainerRef = useRef(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState(0);
  const [scrollStart, setScrollStart] = useState(0);

  const fallbackIcons = {
    all: require("assets/play365/images/games/all games.svg").default,
    video_slots: require("assets/play365/images/games/slots.svg").default,
    bonus_buy: require("assets/play365/images/games/bonus buy.svg").default,
    megaways: require("assets/play365/images/games/megaways.svg").default,
    jackpot: require("assets/play365/images/games/jackpot.svg").default,
    table_games: require("assets/play365/images/games/video table games.svg").default,
    live_casino: require("assets/play365/images/games/live casino.svg").default,
    new: require("assets/play365/images/games/new.svg").default,
  };

  // API-driven strip (per-scope labels/icons/visibility/order); the
  // hardcoded list below stays as the byte-identical fallback when the
  // API is unavailable. API labels arrive translated per scope and render
  // verbatim (never through __()).
  const apiItems = horizontalItems(props.categories);
  const gameCategories = apiItems
    ? apiItems.map((category) => ({
        path: "/games/" + category.route_slug,
        label: category.label,
        // Icon precedence mirrors the left menu: uploaded image > emoji >
        // built-in icon_key > the hardcoded per-slug fallback.
        icon: category.icon_horizontal || null,
        iconEmoji: category.icon_emoji || null,
        iconKey: category.icon_key || null,
        fallbackIcon: fallbackIcons[category.api_slug] || fallbackIcons.video_slots,
        badge: category.badge || null,
        accentHex: resolveAccent(category.accent),
        isFeatured: !!category.is_featured,
        apiLabel: true,
      }))
    : [
    {
      path: "/games/all",
      label: "All Games",
      icon: require("assets/play365/images/games/all games.svg").default
    },
    {
      path: "/games/slots",
      label: "Slots",
      icon: require("assets/play365/images/games/slots.svg").default
    },
    {
      path: "/games/bonus-buy",
      label: "Bonus Buy",
      icon: require("assets/play365/images/games/bonus buy.svg").default
    },
    {
      path: "/games/megaways",
      label: "Megaways",
      icon: require("assets/play365/images/games/megaways.svg").default
    },
    {
      path: "/games/crash",
      label: "Crash Games",
      icon: require("assets/play365/images/games/slots.svg").default // Using slots icon as placeholder
    },
    {
      path: "/games/drops-wins",
      label: "Drops & Wins",
      icon: require("assets/play365/images/games/bonus buy.svg").default // Using bonus buy icon as placeholder
    },
    {
      path: "/games/jackpot",
      label: "Jackpot Games",
      icon: require("assets/play365/images/games/jackpot.svg").default
    },
    {
      path: "/games/table-games",
      label: "Video Table Games",
      icon: require("assets/play365/images/games/video table games.svg").default
    },
    {
      path: "/games/live-casino",
      label: "Live Casino",
      icon: require("assets/play365/images/games/live casino.svg").default
    },
    {
      path: "/games/new",
      label: "New",
      icon: require("assets/play365/images/games/new.svg").default
    }
  ];


  // Check if scrolling is needed and update arrow visibility
  const checkScrollability = () => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const scrollWidth = container.scrollWidth;
      const clientWidth = container.clientWidth;
      const scrollLeft = container.scrollLeft;
      
      // Check if content overflows
      const hasOverflow = scrollWidth > clientWidth;
      
      // Check scroll positions with a small tolerance
      const canScrollLeft = scrollLeft > 5;
      const canScrollRight = scrollLeft < (scrollWidth - clientWidth - 5);
      
      // On tablets and mobile, show arrows if there's overflow
      const isTabletOrMobile = window.innerWidth <= 991;
      
      if (isTabletOrMobile && hasOverflow) {
        setShowLeftArrow(canScrollLeft);
        setShowRightArrow(canScrollRight || !canScrollLeft); // Show right arrow if we can't scroll left
      } else {
        setShowLeftArrow(canScrollLeft);
        setShowRightArrow(canScrollRight);
      }
    }
  };

  // Scroll functions
  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      const scrollAmount = 200; // Adjust scroll distance as needed
      scrollContainerRef.current.scrollBy({
        left: -scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      const scrollAmount = 200; // Adjust scroll distance as needed
      scrollContainerRef.current.scrollBy({
        left: scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  // Mouse drag functionality
  const handleMouseDown = (e) => {
    setIsDragging(true);
    setDragStart(e.clientX);
    setScrollStart(scrollContainerRef.current.scrollLeft);
    e.preventDefault();
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    const deltaX = e.clientX - dragStart;
    scrollContainerRef.current.scrollLeft = scrollStart - deltaX;
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseLeave = () => {
    setIsDragging(false);
  };

  // Touch functionality for mobile
  const handleTouchStart = (e) => {
    setIsDragging(true);
    setDragStart(e.touches[0].clientX);
    setScrollStart(scrollContainerRef.current.scrollLeft);
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    const deltaX = e.touches[0].clientX - dragStart;
    scrollContainerRef.current.scrollLeft = scrollStart - deltaX;
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  // Effect to check scrollability on mount and resize
  useEffect(() => {
    // Initial check with multiple attempts to ensure layout is ready
    const initialCheck = () => {
      checkScrollability();
      // Double-check after a short delay for tablets
      setTimeout(checkScrollability, 200);
      setTimeout(checkScrollability, 500);
    };
    
    initialCheck();
    
    const handleResize = () => {
      setTimeout(checkScrollability, 100);
      setTimeout(checkScrollability, 300); // Additional check for tablets
    };
    
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Effect to check scrollability when scroll position changes
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      const handleScroll = () => {
        checkScrollability();
      };
      
      container.addEventListener('scroll', handleScroll);
      
      // Add MutationObserver to detect layout changes
      const observer = new MutationObserver(() => {
        setTimeout(checkScrollability, 100);
      });
      
      observer.observe(container, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['style', 'class']
      });
      
      return () => {
        container.removeEventListener('scroll', handleScroll);
        observer.disconnect();
      };
    }
  }, []);

  const renderLabel = (label, verbatim) => {
    if (verbatim) {
      return label;
    }
    if (label === "Video Table Games") {
      return (
        <>
          Video Table
          <br />
          Games
        </>
      );
    }
    return __(label);
  };

  const isActive = (category) => {
    // Homepage shows all games — highlight All Games by default
    if (
      category.path === "/games/all" &&
      (location.pathname === "/" || location.pathname === category.path)
    ) {
      return true;
    }
    // Check if current path matches category path
    if (location.pathname === category.path) {
      return true;
    }
    // For providers, also match /providers or /providers/:slug (with or without lang prefix)
    if (category.path === "/providers" && /\/providers(\/|$)/.test(location.pathname)) {
      return true;
    }
    return false;
  };

  return (
    <>
      <div className="gameMenuBar">
        {/* Left Arrow */}
        {showLeftArrow && (
          <button 
            className="nav-arrow nav-arrow-left"
            onClick={scrollLeft}
            aria-label="Scroll left"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        )}
        
        {/* Right Arrow */}
        {showRightArrow && (
          <button 
            className="nav-arrow nav-arrow-right"
            onClick={scrollRight}
            aria-label="Scroll right"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        )}
        
        <nav 
          className="casino-navbar"
          ref={scrollContainerRef}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseLeave}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
        >
          {gameCategories.map((category, index) => (
            <Link
              key={index}
              hreflang={Translator.getSelectedLanguage().code}
              className={
                isActive(category)
                  ? "active nav-item"
                  : "nav-item"
              }
              to={category.path}
              style={category.isFeatured ? { background: (category.accentHex || "#eab308") + "1f", borderRadius: "8px" } : undefined}
            >
              <span className="icon" style={category.accentHex ? { color: category.accentHex } : undefined}>
                {category.icon ? (
                  <img src={category.icon} alt={category.label} width="24" height="24" />
                ) : category.iconEmoji ? (
                  <span style={{ fontSize: "20px", lineHeight: 1 }}>{category.iconEmoji}</span>
                ) : category.iconKey && MENU_ICONS[category.iconKey] ? (
                  MENU_ICONS[category.iconKey]
                ) : (
                  <img src={category.fallbackIcon || category.icon} alt={category.label} width="24" height="24" />
                )}
              </span>
              {/* Pill lives inside the text span: the tab itself is a column
                  flexbox, where a third child would stack below the label. */}
              <span className="text">
                {renderLabel(category.label, category.apiLabel)}
                {badgePill(category.badge)}
              </span>
            </Link>
          ))}
        </nav>
      </div>
    </>
  );
}
